// The productivity score (Pro): one number from habits, tasks and goals over
// the last seven days, each area weighted by how much the user has on in it.
// Shared by Today's ring and the assistant, so both see the same number and
// the assistant can say exactly what would raise it.

import { formatDate } from '../theme';

/**
 * { score, habitScore, taskScore, goalScore, weights: { habits, tasks, goals } },
 * scores 0 to 100 and weights in percent summing to 100.
 */
export const computeProductivityScore = ({ habits = [], completions = {}, tasks = [], archivedTasks = [], goals = [], overdueCount = 0, now = new Date() }) => {
  // === DYNAMIC WEIGHT CALCULATION (Hybrid Approach) ===
  // Base effort scores - habits weighted higher due to daily commitment
  const habitEffort = (habits?.length || 0) * 1.5; // Daily habits = high effort multiplier
  const taskEffort = (tasks || []).filter((t) => !t.completed).length || 0.5; // Active tasks
  const goalEffort = (goals || []).reduce((sum, g) => sum + (g.milestones?.length || 1), 0) || 0.5;

  const totalEffort = habitEffort + taskEffort + goalEffort || 1;

  // Effort-based weights, each kept between 10% and 70%, then re-normalized
  const clamp = (val) => Math.max(0.10, Math.min(0.70, val));
  const habitWeight = clamp(habitEffort / totalEffort);
  const taskWeight = clamp(taskEffort / totalEffort);
  const goalWeight = clamp(goalEffort / totalEffort);
  const weightSum = habitWeight + taskWeight + goalWeight;
  const weights = {
    habits: Math.round((habitWeight / weightSum) * 100),
    tasks: Math.round((taskWeight / weightSum) * 100),
    goals: Math.round((goalWeight / weightSum) * 100),
  };
  // Ensure weights sum to exactly 100 (handle rounding)
  weights.habits += 100 - (weights.habits + weights.tasks + weights.goals);

  // === SCORE CALCULATIONS ===

  // Habit score - Last 7 days completion rate
  const last7Days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    last7Days.push(formatDate(d));
  }
  let habitCompletions7d = 0;
  let habitTotal7d = 0;
  last7Days.forEach((dateStr) => {
    habits.forEach((habit) => {
      habitTotal7d++;
      if (completions[habit.id]?.[dateStr]) habitCompletions7d++;
    });
  });
  const habitScore = habitTotal7d > 0 ? Math.round((habitCompletions7d / habitTotal7d) * 100) : 0;

  // Task score - Completed tasks in last 7 days (including archived)
  const sevenDaysAgo = new Date(now);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const recentTasks = (tasks || []).filter((t) => (t.dueDate ? new Date(t.dueDate) >= sevenDaysAgo : true));
  const recentArchived = (archivedTasks || []).filter((t) => (t.completedAt ? new Date(t.completedAt) >= sevenDaysAgo : false));
  const totalRecentTasks = recentTasks.length + recentArchived.length;
  const completedRecentTasks = recentTasks.filter((t) => t.completed).length + recentArchived.length;
  const baseTaskScore = totalRecentTasks > 0 ? (completedRecentTasks / totalRecentTasks) * 100 : 100;
  // Penalize for overdue tasks (each overdue task reduces score by 5%, max 25%)
  const overduePenalty = Math.min(overdueCount * 5, 25);
  const taskScore = Math.max(0, Math.round(baseTaskScore - overduePenalty));

  // Goal score - Based on total milestone completions across all goals
  let totalMilestones = 0;
  let completedMilestones = 0;
  (goals || []).forEach((goal) => {
    if (goal.milestones?.length) {
      totalMilestones += goal.milestones.length;
      completedMilestones += goal.milestones.filter((m) => m.completed).length;
    }
  });
  const goalScore = totalMilestones > 0 ? Math.round((completedMilestones / totalMilestones) * 100) : (goals?.length > 0 ? 50 : 100);

  // Combined score using dynamic weights
  const score = Math.round(
    (habitScore * weights.habits / 100)
    + (taskScore * weights.tasks / 100)
    + (goalScore * weights.goals / 100),
  );

  return { score, habitScore, taskScore, goalScore, weights };
};
